/* Trafford Signup Modal
   Opens a <dialog data-trafford-signup> from any [data-signup-open="{id}"] trigger,
   animates it in via a CSS class, and submits the no-account customer form via AJAX
   so success shows inline (no page reload). Append-only: the form only creates/tags
   a customer with marketing consent; it never modifies other customer data. */
(function () {
  'use strict';

  function openModal(dialog) {
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') {
      try { dialog.showModal(); } catch (e) { dialog.setAttribute('open', ''); }
    } else {
      dialog.setAttribute('open', '');
    }
    requestAnimationFrame(function () { dialog.classList.add('is-open'); });
  }

  function closeModal(dialog) {
    if (!dialog) return;
    dialog.classList.remove('is-open');
    var panel = dialog.querySelector('.trafford-signup__panel');
    var finished = false;
    var done = function () {
      if (finished) return;
      finished = true;
      if (typeof dialog.close === 'function') {
        try { dialog.close(); } catch (e) { dialog.removeAttribute('open'); }
      } else {
        dialog.removeAttribute('open');
      }
    };
    if (panel) {
      panel.addEventListener('transitionend', done, { once: true });
      setTimeout(done, 400);
    } else {
      done();
    }
  }

  function showSuccess(dialog) {
    if (!dialog) return;
    var formView = dialog.querySelector('[data-signup-view="form"]');
    var successView = dialog.querySelector('[data-signup-view="success"]');
    if (formView) formView.hidden = true;
    if (successView) successView.hidden = false;
  }

  /* Put the dialog back to its form state. Without this the modal stayed on the
     success view for the rest of the page load, so a visitor could only ever sign
     up for ONE product per page: every later trigger opened to "You're on the
     list" with no form. */
  function resetView(dialog) {
    if (!dialog) return;
    var formView = dialog.querySelector('[data-signup-view="form"]');
    var successView = dialog.querySelector('[data-signup-view="success"]');
    if (formView) formView.hidden = false;
    if (successView) successView.hidden = true;
    var err = dialog.querySelector('[data-signup-error]');
    if (err) { err.hidden = true; err.textContent = ''; }
    var form = dialog.querySelector('.trafford-signup__form');
    if (form) {
      var email = form.querySelector('input[type="email"]');
      if (email) email.value = '';
    }
  }

  /* Apply this trigger's copy, restoring the theme default when the trigger does
     not override it. Previously an override persisted for every later trigger, so
     after opening a Core card a Crossroads card still read "when Core launches". */
  function applyCopy(dialog, opener) {
    [
      ['heading', '.trafford-signup__heading'],
      ['subtext', '.trafford-signup__subtext']
    ].forEach(function (pair) {
      var el = dialog.querySelector('[data-signup-view="form"] ' + pair[1]);
      if (!el) return;
      /* remember the theme default the first time we ever touch this element */
      if (el.dataset.defaultCopy === undefined) el.dataset.defaultCopy = el.textContent;
      var override = opener.getAttribute('data-signup-' + pair[0]);
      el.textContent = override || el.dataset.defaultCopy;
    });
  }

  function showError(form, message) {
    var err = form.querySelector('[data-signup-error]');
    if (err) {
      err.hidden = false;
      err.textContent = message || 'Something went wrong. Please try again.';
    }
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-signup-open]');
    if (opener) {
      e.preventDefault();
      var dialog = document.getElementById(opener.getAttribute('data-signup-open'));
      /* per-trigger tags: set the hidden contact[tags] input so one modal serves every product */
      if (dialog) {
        var tag = opener.getAttribute('data-signup-tag');
        if (tag) {
          var tagInput = dialog.querySelector('input[name="contact[tags]"]');
          if (tagInput) tagInput.value = tag;
        }
        /* per-trigger copy override (e.g. Core); falls back to the theme-default modal copy */
        applyCopy(dialog, opener);
        /* always reopen on the form, never on a stale success/error state */
        resetView(dialog);
      }
      openModal(dialog);
      return;
    }
    var closer = e.target.closest('[data-signup-close]');
    if (closer) {
      e.preventDefault();
      closeModal(closer.closest('[data-trafford-signup]'));
      return;
    }
    /* click on the dialog backdrop (the dialog element itself, outside the panel) */
    if (e.target.matches && e.target.matches('[data-trafford-signup]')) {
      closeModal(e.target);
    }
  });

  /* native ESC fires 'cancel' on the dialog */
  document.addEventListener('cancel', function (e) {
    if (e.target.matches && e.target.matches('[data-trafford-signup]')) {
      e.preventDefault();
      closeModal(e.target);
    }
  });

  /* ------------------------------------------------------------------
     SUBMIT: native, NOT AJAX. This is deliberate.

     The store has Shopify spam protection (hCaptcha) enabled for customer
     forms. Shopify's own script attaches a captcha token during a NATIVE
     submit. An AJAX fetch bypasses that flow entirely, so Shopify rejects
     the post with `400 Missing CAPTCHA token` (verified 2026-07-21 by
     posting the form and reading the response).

     That is what produced every symptom at once: Shopify's script still
     submitted the form natively, so the customer was created and the tags
     saved, while our fetch got its 400 and painted an error a fraction of
     a second before the page reloaded. The record was always correct; only
     the UI lied.

     So: let the browser submit. `return_to` brings the visitor back to the
     same page with ?signup=ok, and the success view is restored on load
     below. Do not reintroduce fetch here without first solving the captcha
     token, or this bug comes straight back.
  ------------------------------------------------------------------ */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('.trafford-signup__form');
    if (!form) return;

    /* honeypot: bots fill the hidden "website" field; humans never do.
       Block the post, then show success so the bot learns nothing. */
    var hp = form.querySelector('[name="website"]');
    if (hp && hp.value) {
      e.preventDefault();
      showSuccess(form.closest('[data-trafford-signup]'));
      return;
    }

    /* Strip blank optional fields so Shopify never receives them. A disabled
       input is not submitted, so an empty contact[first_name] cannot reach
       Shopify and cannot blank an existing customer's stored name. */
    form.querySelectorAll('[data-signup-optional]').forEach(function (el) {
      if (!el.value.trim()) el.disabled = true;
    });

    var btn = form.querySelector('[type="submit"]');
    if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }
    /* no preventDefault: the browser (and Shopify's captcha script) take it from here */
  });

  /* On return from a successful post, reopen the modal on its success view and
     tidy the marker out of the URL so a refresh or a shared link does not
     re-trigger it. */
  function restoreSuccessState() {
    if (!/[?&]signup=ok/.test(window.location.search)) return;
    var dialog = document.querySelector('[data-trafford-signup]');
    if (dialog) { showSuccess(dialog); openModal(dialog); }
    if (window.history && window.history.replaceState) {
      var clean = window.location.pathname +
        window.location.search.replace(/([?&])signup=ok&?/, '$1').replace(/[?&]$/, '');
      window.history.replaceState({}, '', clean + window.location.hash.replace('#twc-notify-form', ''));
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreSuccessState);
  } else {
    restoreSuccessState();
  }
})();
